import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Database,
  AlertTriangle,
  FileJson,
  FolderOpen,
  Image,
  CheckCircle2,
} from 'lucide-react';
import {
  mockMONAI15InstanceDetails,
  MONAIDataset,
  MONAIDatasetFormat,
} from '@/mock/gpu-instance';
import { MONAIDatasetManager, MONAIDatasetCard } from '../../components/monai';

const MONAIDatasetsPage = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const [selectedDataset, setSelectedDataset] = useState<MONAIDataset | null>(null);
  const [datasetCardOpen, setDatasetCardOpen] = useState(false);
  const [datasetCardDataset, setDatasetCardDataset] = useState<MONAIDataset | null>(null);

  const details = useMemo(
    () => mockMONAI15InstanceDetails(instanceId || ''),
    [instanceId]
  );

  const handleViewDatasetCard = (dataset: MONAIDataset) => {
    setDatasetCardDataset(dataset);
    setDatasetCardOpen(true);
  };

  const getFormatInfo = (format: MONAIDatasetFormat) => {
    switch (format) {
      case 'nifti':
        return {
          name: 'NIfTI',
          description: 'Neuroimaging format for 3D medical volumes',
          icon: FileJson,
          color: 'text-blue-500',
          bgColor: 'bg-blue-500/10',
          structure: [
            'images/',
            '  case_001.nii.gz',
            '  case_002.nii.gz',
            'labels/',
            '  case_001.nii.gz',
            '  case_002.nii.gz',
          ],
        };
      case 'dicom':
        return {
          name: 'DICOM',
          description: 'Standard medical imaging format with metadata',
          icon: FolderOpen,
          color: 'text-purple-500',
          bgColor: 'bg-purple-500/10',
          structure: [
            'patient_001/',
            '  study_001/',
            '    series_001/',
            '      image_001.dcm',
            '      image_002.dcm',
          ],
        };
      case 'png':
        return {
          name: 'PNG/JPEG',
          description: '2D medical images for classification tasks',
          icon: Image,
          color: 'text-green-500',
          bgColor: 'bg-green-500/10',
          structure: [
            'train/',
            '  class_1/',
            '    image_001.png',
            '  class_2/',
            '    image_002.png',
          ],
        };
      case 'medmnist':
        return {
          name: 'MedMNIST',
          description: 'Standardized benchmark datasets in NPZ format',
          icon: Database,
          color: 'text-orange-500',
          bgColor: 'bg-orange-500/10',
          structure: [
            'pathmnist.npz',
            '  train_images: (89996, 28, 28, 3)',
            '  train_labels: (89996,)',
            '  val_images: (10004, 28, 28, 3)',
            '  test_images: (7180, 28, 28, 3)',
          ],
        };
    }
  };

  // Statistics
  const segmentationDatasets = details.datasets.filter(
    (d) => d.task === 'segmentation'
  ).length;
  const classificationDatasets = details.datasets.filter(
    (d) => d.task === 'classification'
  ).length;
  const totalSamples = details.datasets.reduce(
    (acc, d) => acc + d.trainCount + d.valCount + (d.testCount || 0),
    0
  );

  return (
    <div className="container py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="h-6 w-6 text-blue-500" />
            MONAI Datasets
          </h1>
          <p className="text-muted-foreground">
            Medical imaging datasets for MONAI training
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold">{details.datasets.length}</div>
            <div className="text-sm text-muted-foreground">Total Datasets</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-purple-600">
              {segmentationDatasets}
            </div>
            <div className="text-sm text-muted-foreground">Segmentation</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-blue-600">
              {classificationDatasets}
            </div>
            <div className="text-sm text-muted-foreground">Classification</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold">
              {totalSamples.toLocaleString()}
            </div>
            <div className="text-sm text-muted-foreground">Total Samples</div>
          </CardContent>
        </Card>
      </div>

      {/* Format Guide */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Supported Formats</CardTitle>
          <CardDescription>
            MONAI supports multiple medical imaging formats
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            {(['nifti', 'dicom', 'png', 'medmnist'] as MONAIDatasetFormat[]).map(
              (format) => {
                const info = getFormatInfo(format);
                const Icon = info.icon;
                return (
                  <Card key={format} className="bg-muted/30">
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`p-2 rounded-lg ${info.bgColor}`}>
                          <Icon className={`h-5 w-5 ${info.color}`} />
                        </div>
                        <div>
                          <div className="font-medium">{info.name}</div>
                          <div className="text-xs text-muted-foreground">{info.description}</div>
                        </div>
                      </div>
                      <div className="bg-background rounded p-2">
                        <pre className="text-xs font-mono text-muted-foreground whitespace-pre">
                          {info.structure.join('\n')}
                        </pre>
                      </div>
                    </CardContent>
                  </Card>
                );
              }
            )}
          </div>
        </CardContent>
      </Card>

      {/* Dataset Browser */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Available Datasets</CardTitle>
          <CardDescription>
            Click on a dataset to view details and select for training
          </CardDescription>
        </CardHeader>
        <CardContent>
          <MONAIDatasetManager
            datasets={details.datasets}
            selectedDatasetId={selectedDataset?.id}
            onSelectDataset={setSelectedDataset}
            onViewDatasetCard={handleViewDatasetCard}
          />
        </CardContent>
      </Card>

      {/* Selected Dataset Indicator */}
      {selectedDataset && (
        <Card className="border-primary/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                Selected: {selectedDataset.displayName}
              </CardTitle>
              <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Ready to Use
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
              <div className="p-3 bg-muted/50 rounded-lg text-center">
                <div className="text-xs text-muted-foreground">Train</div>
                <div className="font-mono font-bold">
                  {selectedDataset.trainCount.toLocaleString()}
                </div>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg text-center">
                <div className="text-xs text-muted-foreground">Validation</div>
                <div className="font-mono font-bold">
                  {selectedDataset.valCount.toLocaleString()}
                </div>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg text-center">
                <div className="text-xs text-muted-foreground">Test</div>
                <div className="font-mono font-bold">
                  {selectedDataset.testCount?.toLocaleString() || '-'}
                </div>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg text-center">
                <div className="text-xs text-muted-foreground">Classes</div>
                <div className="font-mono font-bold">
                  {selectedDataset.classes.length}
                </div>
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-2">
                Classes ({selectedDataset.classes.length}):
              </div>
              <div className="flex flex-wrap gap-1">
                {selectedDataset.classes.map((cls) => (
                  <Badge key={cls} variant="secondary" className="text-xs">
                    {cls}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="text-sm text-muted-foreground mb-1">Path</div>
              <code className="text-sm font-mono">{selectedDataset.path}</code>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dataset Card Modal */}
      <MONAIDatasetCard
        dataset={datasetCardDataset}
        open={datasetCardOpen}
        onOpenChange={setDatasetCardOpen}
        onSelectDataset={(dataset) => {
          setSelectedDataset(dataset);
          setDatasetCardOpen(false);
        }}
      />

      {/* Demo Warning */}
      <Alert className="border-yellow-500/50 bg-yellow-500/5">
        <AlertTriangle className="h-4 w-4 text-yellow-600" />
        <AlertDescription className="text-yellow-700">
          <span className="font-medium">Demo Mode:</span> These are pre-loaded
          datasets (MedMNIST + Medical Decathlon) for demonstration. In
          production, you can upload your own datasets in NIfTI, DICOM, or image
          formats.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default MONAIDatasetsPage;
